import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
interface Viewing {
  id: string;
  date: string;
  time: string;
  status: 'scheduled' | 'completed' | 'cancelled';
}
interface MiniCalendarProps {
  viewings: Viewing[];
  onDateClick?: (date: string) => void;
}
export function MiniCalendar({
  viewings,
  onDateClick
}: MiniCalendarProps) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const monthNames = ['Ianuarie', 'Februarie', 'Martie', 'Aprilie', 'Mai', 'Iunie', 'Iulie', 'August', 'Septembrie', 'Octombrie', 'Noiembrie', 'Decembrie'];
  const daysOfWeek = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];
  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    return new Date(year, month + 1, 0).getDate();
  };
  const getFirstDayOfMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    return firstDay === 0 ? 6 : firstDay - 1; // Convert Sunday (0) to 6, Monday (1) to 0, etc.
  };
  const previousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
  };
  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
  };
  const getViewingsForDate = (day: number) => {
    const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return viewings.filter(v => v.date === dateStr);
  };
  const isToday = (day: number) => {
    const today = new Date();
    return day === today.getDate() && currentDate.getMonth() === today.getMonth() && currentDate.getFullYear() === today.getFullYear();
  };
  const daysInMonth = getDaysInMonth(currentDate);
  const firstDayOfMonth = getFirstDayOfMonth(currentDate);
  const days = Array.from({
    length: daysInMonth
  }, (_, i) => i + 1);
  const emptyDays = Array.from({
    length: firstDayOfMonth
  }, (_, i) => i);
  return <div className="bg-white rounded-2xl p-4" data-id="element-4129">
      {/* Calendar Header */}
      <div className="flex items-center justify-between mb-4" data-id="element-4130">
        <button onClick={previousMonth} className="p-2 hover:bg-slate-100 rounded-lg transition-colors" data-id="element-4131">
          <ChevronLeftIcon className="w-4 h-4 text-slate-600" data-id="element-4132" />
        </button>
        <h3 className="text-sm font-bold text-[#132C53]" data-id="element-4133">
          {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
        </h3>
        <button onClick={nextMonth} className="p-2 hover:bg-slate-100 rounded-lg transition-colors" data-id="element-4134">
          <ChevronRightIcon className="w-4 h-4 text-slate-600" data-id="element-4135" />
        </button>
      </div>

      {/* Days of Week */}
      <div className="grid grid-cols-7 gap-1 mb-2" data-id="element-4136">
        {daysOfWeek.map((day, index) => <div key={index} className="text-center text-xs font-bold text-slate-500 py-1" data-id="element-4137">
            {day}
          </div>)}
      </div>

      {/* Calendar Days */}
      <div className="grid grid-cols-7 gap-1" data-id="element-4138">
        {/* Empty cells for days before month starts */}
        {emptyDays.map((_, index) => <div key={`empty-${index}`} className="aspect-square" data-id="element-4139" />)}

        {/* Actual days */}
        {days.map(day => {
        const dayViewings = getViewingsForDate(day);
        const hasScheduled = dayViewings.some(v => v.status === 'scheduled');
        const hasCompleted = dayViewings.some(v => v.status === 'completed');
        const today = isToday(day);
        return <motion.button key={day} whileHover={{
          scale: 1.05
        }} whileTap={{
          scale: 0.95
        }} onClick={() => {
          const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
          onDateClick?.(dateStr);
        }} className={`
                aspect-square rounded-lg flex flex-col items-center justify-center text-xs font-medium transition-all relative
                ${today ? 'bg-gradient-to-br from-blue-500 to-indigo-600 text-white shadow-md' : 'hover:bg-slate-100'}
                ${!today && dayViewings.length > 0 ? 'bg-slate-50' : ''}
              `} data-id="element-4140">
              <span className={today ? 'font-bold' : 'text-slate-700'} data-id="element-4141">
                {day}
              </span>

              {/* Viewing indicators */}
              {dayViewings.length > 0 && !today && <div className="flex gap-0.5 mt-0.5" data-id="element-4142">
                  {hasScheduled && <div className="w-1 h-1 rounded-full bg-blue-500" data-id="element-4143" />}
                  {hasCompleted && <div className="w-1 h-1 rounded-full bg-emerald-500" data-id="element-4144" />}
                </div>}

              {/* Count badge for multiple viewings */}
              {dayViewings.length > 1 && <div className="absolute -top-1 -right-1 w-4 h-4 bg-gradient-to-br from-orange-500 to-red-600 rounded-full flex items-center justify-center text-white text-[10px] font-bold shadow-md" data-id="element-4145">
                  {dayViewings.length}
                </div>}
            </motion.button>;
      })}
      </div>

      {/* Legend */}
      <div className="mt-4 pt-4 border-t border-slate-100 space-y-2" data-id="element-4146">
        <div className="flex items-center gap-2 text-xs" data-id="element-4147">
          <div className="w-2 h-2 rounded-full bg-blue-500" data-id="element-4148" />
          <span className="text-slate-600" data-id="element-4149">Programate</span>
        </div>
        <div className="flex items-center gap-2 text-xs" data-id="element-4150">
          <div className="w-2 h-2 rounded-full bg-emerald-500" data-id="element-4151" />
          <span className="text-slate-600" data-id="element-4152">Finalizate</span>
        </div>
      </div>
    </div>;
}